import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../authentication/authentication.service';
import { APP_CONSTANTS } from '../../constants/app.constants';

// Define base options type
export interface BaseHttpOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | string[] };
  reportProgress?: boolean;
  withCredentials?: boolean;
}

// Specific options types for overloading
export interface JsonHttpOptions extends BaseHttpOptions {
  responseType?: 'json';
}
export interface TextHttpOptions extends BaseHttpOptions {
  responseType: 'text';
}
export interface BlobHttpOptions extends BaseHttpOptions {
  responseType: 'blob';
}
export interface ArrayBufferHttpOptions extends BaseHttpOptions {
  responseType: 'arraybuffer';
}

export type AnyHttpOptions =
  | JsonHttpOptions
  | TextHttpOptions
  | BlobHttpOptions
  | ArrayBufferHttpOptions;

// Response wrapper interface
export interface ApiResponse<T> {
  result?: T;
  data?: T;
  success?: boolean;
  message?: string;
  error?: string;
}

// Error response interface
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // Modern Angular 19 inject() function (alternative to constructor DI)
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly baseUrl = environment.API_ENDPOINT;

  constructor() {
    if (!this.baseUrl) {
      console.error('API URL not set in environment configuration!');
    }
  }

  // --- GET Overloads ---
  get<T>(endpoint: string, options?: JsonHttpOptions): Observable<T>;
  get(endpoint: string, options: TextHttpOptions): Observable<string>;
  get(endpoint: string, options: BlobHttpOptions): Observable<Blob>;
  get(
    endpoint: string,
    options: ArrayBufferHttpOptions
  ): Observable<ArrayBuffer>;
  get(endpoint: string, options?: AnyHttpOptions): Observable<any> {
    const url = this.buildUrl(endpoint);
    return this.http.get(url, options as any).pipe(
      map((response: any) => this.extractData(response)),
      catchError((error) => this.handleError(error))
    );
  }

  // --- POST Overloads ---
  post<T>(
    endpoint: string,
    body: any | null,
    options?: JsonHttpOptions
  ): Observable<T>;
  post(
    endpoint: string,
    body: any | null,
    options: TextHttpOptions
  ): Observable<string>;
  post(
    endpoint: string,
    body: any | null,
    options: BlobHttpOptions
  ): Observable<Blob>;
  post(
    endpoint: string,
    body: any | null,
    options: ArrayBufferHttpOptions
  ): Observable<ArrayBuffer>;
  post(
    endpoint: string,
    body: any | null,
    options?: AnyHttpOptions
  ): Observable<any> {
    const url = this.buildUrl(endpoint);
    return this.http.post(url, body, options as any).pipe(
      map((response: any) => this.extractData(response)),
      catchError((error) => this.handleError(error))
    );
  }

  // --- PUT Overloads ---
  put<T>(
    endpoint: string,
    body: any | null,
    options?: JsonHttpOptions
  ): Observable<T>;
  put(
    endpoint: string,
    body: any | null,
    options: TextHttpOptions
  ): Observable<string>;
  put(
    endpoint: string,
    body: any | null,
    options: BlobHttpOptions
  ): Observable<Blob>;
  put(
    endpoint: string,
    body: any | null,
    options: ArrayBufferHttpOptions
  ): Observable<ArrayBuffer>;
  put(
    endpoint: string,
    body: any | null,
    options?: AnyHttpOptions
  ): Observable<any> {
    const url = this.buildUrl(endpoint);
    return this.http.put(url, body, options as any).pipe(
      map((response: any) => this.extractData(response)),
      catchError((error) => this.handleError(error))
    );
  }

  // --- PATCH method (commonly used in REST APIs) ---
  patch<T>(
    endpoint: string,
    body: any | null,
    options?: JsonHttpOptions
  ): Observable<T>;
  patch(
    endpoint: string,
    body: any | null,
    options: TextHttpOptions
  ): Observable<string>;
  patch(
    endpoint: string,
    body: any | null,
    options: BlobHttpOptions
  ): Observable<Blob>;
  patch(
    endpoint: string,
    body: any | null,
    options: ArrayBufferHttpOptions
  ): Observable<ArrayBuffer>;
  patch(
    endpoint: string,
    body: any | null,
    options?: AnyHttpOptions
  ): Observable<any> {
    const url = this.buildUrl(endpoint);
    return this.http.patch(url, body, options as any).pipe(
      map((response: any) => this.extractData(response)),
      catchError((error) => this.handleError(error))
    );
  }

  // --- DELETE Overloads ---
  delete<T>(endpoint: string, options?: JsonHttpOptions): Observable<T>;
  delete(endpoint: string, options: TextHttpOptions): Observable<string>;
  delete(endpoint: string, options: BlobHttpOptions): Observable<Blob>;
  delete(
    endpoint: string,
    options: ArrayBufferHttpOptions
  ): Observable<ArrayBuffer>;
  delete(endpoint: string, options?: AnyHttpOptions): Observable<any> {
    const url = this.buildUrl(endpoint);
    return this.http.delete(url, options as any).pipe(
      map((response: any) => this.extractData(response)),
      catchError((error) => this.handleError(error))
    );
  }

  // --- Utility Methods ---

  /**
   * Build complete URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    // Handle endpoints that already start with baseUrl or are absolute
    if (endpoint.startsWith('http') || endpoint.startsWith(this.baseUrl)) {
      return endpoint;
    }

    // Ensure proper URL joining
    const base = this.baseUrl.endsWith('/')
      ? this.baseUrl.slice(0, -1)
      : this.baseUrl;
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${base}${path}`;
  }

  /**
   * Extract data from response wrapper
   */
  private extractData(response: any): any {
    // Handle different response wrapper patterns
    if (response === null || response === undefined) {
      return response;
    }

    // Check common response wrapper patterns
    if (response.hasOwnProperty('result')) {
      return response.result;
    }

    if (response.hasOwnProperty('data')) {
      return response.data;
    }

    // Return response as-is if no wrapper detected
    return response;
  }

  /**
   * Enhanced error handler with better error structure
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let apiError: ApiError;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      apiError = {
        message: `Client Error: ${error.error.message}`,
        status: 0,
        code: 'CLIENT_ERROR',
      };
    } else {
      // Server-side error
      const backendMessage = this.extractBackendMessage(error);
      apiError = {
        message: backendMessage || `Server Error: ${error.status || 'Unknown'} - ${error.statusText || 'Unknown'}`,
        status: error.status || 0,
        code: this.getErrorCode(error.status || 0),
        details: error.error,
      };
    }

    // Log error for debugging
    console.error('ApiService Error:', error);
    console.error('Processed Error:', apiError);

    return throwError(() => apiError);
  }

  /**
   * Extract meaningful message from backend error
   */
  private extractBackendMessage(errorBody: any): string {
    if (!errorBody) return '';

    // Check common error message patterns
    if (typeof errorBody === 'string') {
      return errorBody;
    }

    if (typeof errorBody === 'object') {
      const message = errorBody.message || errorBody.error || errorBody.detail || errorBody.title;
      return message || '';
    }

    return '';
  }

  /**
   * Get error code based on HTTP status
   */
  private getErrorCode(status: number): string {
    switch (status) {
      case 400:
        return 'BAD_REQUEST';
      case 401:
        return 'UNAUTHORIZED';
      case 403:
        return 'FORBIDDEN';
      case 404:
        return 'NOT_FOUND';
      case 409:
        return 'CONFLICT';
      case 422:
        return 'VALIDATION_ERROR';
      case 500:
        return 'INTERNAL_SERVER_ERROR';
      case 502:
        return 'BAD_GATEWAY';
      case 503:
        return 'SERVICE_UNAVAILABLE';
      default:
        return 'UNKNOWN_ERROR';
    }
  }

  // --- Convenience Methods ---

  /**
   * Upload file with progress tracking
   */
  uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: any
  ): Observable<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.keys(additionalData).forEach((key) => {
        formData.append(key, additionalData[key]);
      });
    }

    return this.post<T>(endpoint, formData, {
      reportProgress: true,
    });
  }

  /**
   * Download file as blob
   */
  downloadFile(endpoint: string, filename?: string): Observable<Blob> {
    return this.get(endpoint, { responseType: 'blob' }).pipe(
      map((blob: Blob) => {
        if (filename) {
          // Trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        return blob;
      })
    );
  }

  /**
   * Stream response for Server-Sent Events
   */
  postStream(endpoint: string, body: any): Observable<Response> {
    let url = this.buildUrl(endpoint);
    const token = this.authService.getCookie(APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN);
    
    // Clean URL by removing credentials if present
    url = url.replace(/\/\/[^@]+@/, '//');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return new Observable(observer => {
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        observer.next(response);
        observer.complete();
      })
      .catch(error => {
        console.error('Stream fetch error:', error);
        observer.error(this.createStreamError(error));
      });
    });
  }

  private createStreamError(error: any): ApiError {
    return {
      message: error.message || 'Stream request failed',
      status: 0,
      code: 'STREAM_ERROR',
    };
  }
}
